/**
 * SA-717 (spec §5): the Inbox's kind and flag chips, as data. One recipe, on
 * the basis Badge's soft variants (SA-468): a policy kind is info, the two
 * built-in kinds are muted, unknown is a warning. Only the flags a person
 * acts on, or reads at a glance, are chips; the rest stay filters.
 */
export type ChipVariant = 'info-soft' | 'muted-soft' | 'warning-soft' | 'success-soft' | 'destructive-soft'

export interface TriageChip { key: string; label: string; variant: ChipVariant }

const QUIET_KINDS = new Set(['internal', 'automated'])

export function kindChip(kind: string | null, label: string | null): TriageChip | null {
  if (!kind) return null
  const variant: ChipVariant = kind === 'unknown' ? 'warning-soft' : QUIET_KINDS.has(kind) ? 'muted-soft' : 'info-soft'
  return { key: `kind:${kind}`, label: label || kind, variant }
}

// In display order.
const FLAG_CHIPS: Array<[string, string, ChipVariant]> = [
  ['awaiting_approval', 'Awaiting approval', 'warning-soft'],
  ['needs_triage', 'Needs a kind', 'warning-soft'],
  ['handler_failed', 'Handler failed', 'destructive-soft'],
  ['import_pending', 'Import pending', 'warning-soft'],
  ['record_created', 'Record created', 'success-soft'],
  ['rejected', 'Rejected', 'muted-soft'],
]

export function flagChips(flags: readonly string[]): TriageChip[] {
  const held = new Set(flags)
  return FLAG_CHIPS.filter(([flag]) => held.has(flag)).map(([flag, label, variant]) => ({ key: `flag:${flag}`, label, variant }))
}

/** SA-946: a WhatsApp group thread has no kind (SA-945 S1); its row says how
 * many messages wait for a person in place of the two waiting flags, and
 * keeps "Handler failed". */
export function groupChips(flags: readonly string[], waiting: number): TriageChip[] {
  const count: TriageChip[] = waiting > 0 ? [{ key: 'waiting', label: `${waiting} waiting`, variant: 'warning-soft' }] : []
  return [...count, ...flagChips(flags.filter((flag) => flag === 'handler_failed'))]
}

export function rowChips(conv: {
  kind: string | null; kindLabel: string | null; flags: readonly string[]; channel?: string; waitingCount?: number | null
}): TriageChip[] {
  if (conv.channel === 'whatsapp-group') return groupChips(conv.flags, conv.waitingCount ?? 0)
  const kind = kindChip(conv.kind, conv.kindLabel)
  return [...(kind ? [kind] : []), ...flagChips(conv.flags)]
}
